import {
  CheckCircleOutlined,
  DeleteTwoTone,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import React, { useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { Button, Input, Space, Table, Tag } from "antd";
import { Link } from "react-router-dom";
import ResponsiveDialog from "../dialog/dialog";
import { useNavigate } from "react-router-dom";
import customerService from "../../service/serviceCustomer";
import { formatCnpjCpf } from "../../utils/formatters";
import { TYPE_CLIENT, TYPE_PERSON } from "../../constants/customerTypes";

const TableCustomer = ({ data, onPageChange, onFilterChange, onSortChange }) => {
  const usuario = JSON.parse(localStorage.getItem("user_token")) || {};
  const permission = usuario.roles?.filter((item) => item.name == "Cliente")[0];

  let navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const [activeFilters, setActiveFilters] = useState({});

  const [openDialog, setOpenDialog] = useState();
  const [messageDialog, setMessageDialog] = useState();
  const [titleDialog, setTitleDialog] = useState();
  const [statusDialog, setStatusDialog] = useState();

  function refreshPage() {
    window.location.reload(false);
  }

  /**
   * @function mapColumnToBackend
   * @description Mapear nomes das colunas do frontend para o backend
   * @param {string} columnName - Nome da coluna do frontend
   * @returns 
   */
  const mapColumnToBackend = (columnName) => {
    const mapping = {
      'idcustomer': 'idcustomer',
      'name': 'name',
      'cpfcnpj': 'cpfCnpj',
      'typeperson': 'typePerson',
      'typeclient': 'typeClient',
      'username': 'user',
      'codexterno': 'codExterno',
      'isactive': 'isActive',
    };
    
    const normalizedColumn = columnName.toLowerCase();
    return mapping[normalizedColumn] || columnName;
  };

  /**
   * @function handleTableChange
   * @description Manipula mudanças na tabela (paginação, ordenação, filtros)
   */
  const handleTableChange = (pagination, filters, sorter) => {
    console.log("🔄 [TableCustomer] Mudança na tabela:", { pagination, filters, sorter });
    
    // Se houver ordenação e onSortChange foi passado
    if (sorter && sorter.columnKey && onSortChange) {
      const sortBy = mapColumnToBackend(sorter.columnKey);
      const sortOrder = sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : null;
      
      console.log("📊 [TableCustomer] Ordenação:", { 
        coluna: sorter.columnKey, 
        sortBy, 
        sortOrder 
      });
      
      if (sortOrder) {
        onSortChange(sortBy, sortOrder);
      }
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex, close) => {
    console.log("🔍 [TableCustomer] Aplicando filtro:", dataIndex, "=", selectedKeys[0]);
    
    if (selectedKeys[0] && selectedKeys[0].trim() !== "") {
      // Criar novo objeto de filtros ANTES de atualizar o estado
      const newFilters = {
        ...activeFilters,
        [dataIndex]: selectedKeys[0]
      };
      
      const backendFilters = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        const backendKey = mapColumnToBackend(key);
        backendFilters[backendKey] = value;
      });
      
      console.log("📡 [TableCustomer] Filtros frontend:", newFilters);
      console.log("📡 [TableCustomer] Filtros backend:", backendFilters);
      
      // Notificar componente pai IMEDIATAMENTE (antes de atualizar estados)
      if (onFilterChange) {
        onFilterChange(backendFilters);
      }
      
      // DEPOIS atualizar estados locais (apenas para UI)
      setActiveFilters(newFilters);
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
    }
    
    // Fechar dropdown sem confirmar filtro (porque não tem onFilter)
    if (close) {
      close();
    }
  };

  const handleReset = (clearFilters, dataIndex) => {
    console.log("🧹 [TableCustomer] Limpando filtro:", dataIndex);
    
    if (clearFilters) {
      clearFilters();
    }
    
    if (searchedColumn === dataIndex) {
      setSearchText("");
      setSearchedColumn("");
    }

    const newFilters = { ...activeFilters };
    delete newFilters[dataIndex];
    setActiveFilters(newFilters);

    const backendFilters = {};
    Object.entries(newFilters).forEach(([key, value]) => {
      const backendKey = mapColumnToBackend(key);
      backendFilters[backendKey] = value;
    });

    // Notificar componente pai sobre mudança nos filtros
    if (onFilterChange) {
      console.log("📡 [TableCustomer] Filtros frontend após reset:", newFilters);
      console.log("📡 [TableCustomer] Filtros backend após reset:", backendFilters);
      onFilterChange(backendFilters);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Pesquisar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex, close)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />

        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex, close)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Procurar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Limpar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Fechar
          </Button>
        </Space>
      </div>
    ),

    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: activeFilters[dataIndex] ? "#1677ff" : undefined,
        }}
      />
    ),

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const showStatusTypePerson = (status) => {
    console.log("Tipo pessoa recebido:", status);
    
    // Mapeamento direto para debug
    const personMap = {
      "Pessoa Física": "geekblue",
      "Pessoa Jurídica": "success",
      "Estrangeiro": "purple",
      "pessoa física": "geekblue",
      "pessoa jurídica": "success",
      "estrangeiro": "purple"
    };
    
    const color = personMap[status] || "success";
    console.log("Cor pessoa retornada:", color, "para status:", status);
    return color;
  };

  const showStatusTypeClient = (status) => {
    console.log("Status recebido:", status);
    
    // Mapeamento direto para debug - incluindo variações possíveis
    const statusMap = {
      "Agencia": "geekblue",
      "Anunciante": "warning", 
      "Broker": "success",
      "Locatario": "error",
      "Fornecedor": "processing",
      "agencia": "primary",
      "anunciante": "warning",
      "broker": "success", 
      "locatario": "error",
      "fornecedor": "processing",
      "Agência": "primary",
      "Locatário": "error"
    };
    
    const color = statusMap[status] || "default";
    console.log("Cor retornada:", color, "para status:", status);
    return color;
  };

  async function deleteCustomer(id) {
    try {
      const response = await customerService.delete(id);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.success === true
      ) {
        setOpenDialog(true);
        setStatusDialog("success");
        setTitleDialog("Muito bem!");
        setMessageDialog("Cliente " + id + " deletado com sucesso");
      } else {
        setOpenDialog(true);
        setStatusDialog("error");
        setTitleDialog("Ops!");
        setMessageDialog(
          "Erro ao excluir o cliente " +
            id +
            ", verifique se o mesmo encontra-se inserida em alguma Venda. "
        );
      }
    } catch (error) {
      setOpenDialog(true);
      setStatusDialog("error");
      setTitleDialog("Ops!");
      setMessageDialog(
        error.message ||
          "Erro ao excluir o cliente " +
            id +
            ", verifique se o mesmo encontra-se inserida em alguma Venda. "
      );
    }
  }

  const columns = [
    {
      title: "Código",
      dataIndex: "idcustomer",
      width: "13%",
      key: "idcustomer",
      // ...getColumnSearchProps('idcustomer'),
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      width: "55%",
      ...getColumnSearchProps("name"),
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },

    {
      title: "CNPJ",
      dataIndex: "cpfCnpj",
      key: "cpfCnpj",
      width: "55%",
      ...getColumnSearchProps("cpfCnpj"),
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (cnpj) => <>{formatCnpjCpf(cnpj)}</>,
    },
    {
      title: "Tipo Pessoa",
      dataIndex: "typePerson",
      key: "typePerson",
      ...getColumnSearchProps('typePerson'),
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (typePerson) => (
        <span>
          {
            <Tag color={showStatusTypePerson(typePerson)} key={1}>
              {typePerson}
            </Tag>
          }
        </span>
      ),
    },
    {
      title: "Tipo Cliente",
      dataIndex: "typeClient",
      key: "typeClient",
      ...getColumnSearchProps('typeClient'),
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (typeClient) => (
        <span>
          {
            <Tag color={showStatusTypeClient(typeClient)} key={1}>
              {typeClient}
            </Tag>
          }
        </span>
      ),
    },
    {
      title: "Ações",
      // dataIndex: 'data',
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Link to={"/customer/" + record.idcustomer}>
            {permission?.isinsert ? (
              <EditOutlined
                style={{
                  fontSize: "20px",
                  color: "#4169E1",
                }}
              />
            ) : (
              <EyeOutlined
                style={{
                  fontSize: "20px",
                  color: "#4169E1",
                }}
              />
            )}
          </Link>
          {permission?.isremove ? (
            <Link to={"#"}>
              <DeleteTwoTone
                twoToneColor="#DC143C"
                style={{
                  fontSize: "20px",
                }}
                onClick={(e) => deleteCustomer(record.idcustomer)}
              />
            </Link>
          ) : (
            ""
          )}
          {record.codExterno ? (
            <Link to={"#"}>
              <CheckCircleOutlined
                titleAccess={"Cliente integrado"}
                color="success"
              />
            </Link>
          ) : (
            ""
          )}
        </Space>
      ),
    },
  ];

  const handleDialogClose = () => {
    setOpenDialog(false);
    navigate("/listCustomer");
    refreshPage();
  };

  return (
    <>
      <ResponsiveDialog
        open={openDialog}
        handleClose={handleDialogClose}
        title={titleDialog}
        message={messageDialog}
        status={statusDialog}
      />

      <Table 
        columns={columns} 
        dataSource={Array.isArray(data?.data) ? data.data : []} 
        rowKey="idcustomer"
        onChange={handleTableChange}
        pagination={{
          current: data?.pagination?.currentPage || 1,
          pageSize: data?.pagination?.itemsPerPage || 10,
          total: data?.pagination?.totalItems || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            console.log("🔄 [TableCustomer] Mudando para página:", page, "Tamanho:", pageSize);
            if (onPageChange) {
              onPageChange(page, pageSize);
            }
          },
          onShowSizeChange: (current, size) => {
            console.log("🔄 [TableCustomer] Mudando tamanho da página:", size);
            if (onPageChange) {
              onPageChange(1, size);
            }
          }
        }}
      />
    </>
  );
};

export default TableCustomer;
